// Fórmulas de la Plantilla PRM (portadas de la demo prmModel.ts a JS plano).
// Los totales NO se persisten: se calculan aquí, en una sola pasada sobre el árbol.
//
//   deviations   = Σ meses
//   totalCo      = Σ (CO × peso)        peso 1 confirmada, 0 prevista (según escenario)
//   totalEstimate = ep + deviations + totalCo
//   margin €     = ec − totalEstimate
//   margin %     = margin / ec          (fracción; 0 si ec = 0)
//
// Obra en curso: los datos manuales son el incurrido total (it) y el acumulado (ac)
// —gasto real, no derivable del encaje—; de ellos salen las columnas calculadas:
//   incurredMonth = incurredTotal − accumulated    (Incurrido Mes)
//   pendingCost   = totalEstimate − incurredTotal   (Costes ptes para finalizar)
//   workPct       = incurredTotal / totalEstimate   (Obra %; fracción, 0 si coste = 0)
//
// Convención VACÍO: null = celda sin valor (se pinta con rayita); un número, incluido
// 0, = valor real. Las operaciones propagan el vacío: si TODOS los operandos están
// vacíos el resultado es vacío (null); si alguno tiene valor, los vacíos cuentan como 0.
// Así una celda calculada solo queda vacía si ninguno de sus insumos tiene valor.

const present = (v) => v !== null && v !== undefined

// Suma que preserva el vacío (null si ningún operando tiene valor).
function add (vals) {
  let sum = null
  vals.forEach((v) => { if (present(v)) sum = (sum || 0) + v })
  return sum
}

// Resta que preserva el vacío (null solo si ambos operandos están vacíos).
function sub (a, b) {
  return (!present(a) && !present(b)) ? null : (a || 0) - (b || 0)
}

// Porcentaje que preserva el vacío: vacío solo si numerador y denominador lo están.
function ratio (num, den) {
  if (!present(num) && !present(den)) return null
  return den ? (num || 0) / den : 0
}

// Pesos por CO según el escenario (confirmed | expected | both).
export function coWeights (cos, scenario) {
  const w = {}
  cos.forEach((c) => {
    const confirmed = (c.status || 'confirmed') === 'confirmed'
    w[c.id] = confirmed
      ? (scenario === 'expected' ? 0 : 1)
      : (scenario === 'confirmed' ? 0 : 1)
  })
  return w
}

// Cálculos derivados de una fila (hoja o acumulado de grupo). Los campos vacíos
// llegan como null/undefined y se propagan como tal.
function derive (raw, monthIds, coIds, weights) {
  const deviations = add(monthIds.map((id) => raw.m[id]))
  const totalCo = add(coIds.map((id) => (present(raw.co[id]) ? raw.co[id] * (weights[id] != null ? weights[id] : 1) : null)))
  const totalEstimate = add([raw.ep, deviations, totalCo])
  // Desviación Plan Comercial = encaje comercial − encaje inicial (las dos primeras columnas).
  const commercialDeviation = sub(raw.ec, raw.ep)
  const margin = sub(raw.ec, totalEstimate)
  const incurredTotal = present(raw.it) ? raw.it : null
  const accumulated = present(raw.ac) ? raw.ac : null
  return {
    ec: present(raw.ec) ? raw.ec : null,
    ep: present(raw.ep) ? raw.ep : null,
    months: raw.m,
    cos: raw.co,
    deviations,
    commercialDeviation,
    totalCo,
    totalEstimate,
    // Desviación "Inicial proyectos - Revisado" = encaje inicial − coste total estimado.
    deviationInitial: sub(raw.ep, totalEstimate),
    margin,
    marginPct: present(margin) ? (raw.ec ? margin / raw.ec : 0) : null,
    incurredTotal,
    accumulated,
    incurredMonth: sub(incurredTotal, accumulated),
    pendingCost: sub(totalEstimate, incurredTotal),
    workPct: ratio(incurredTotal, totalEstimate),
  }
}

// Calcula todos los nodos en una pasada. Devuelve { nodeId: computed }.
export function computeAll (structure, values, monthIds, coIds, weights) {
  const map = {}
  const visit = (node) => {
    let raw
    if (node.children && node.children.length) {
      const acc = { ec: null, ep: null, m: {}, co: {}, it: null, ac: null }
      node.children.forEach((child) => {
        const c = visit(child)
        // Filas informativas (Nº de horas): se calculan para mostrarse pero NO suman.
        if (child.info) return
        acc.ec = add([acc.ec, c.ec])
        acc.ep = add([acc.ep, c.ep])
        acc.it = add([acc.it, c.incurredTotal])
        acc.ac = add([acc.ac, c.accumulated])
        monthIds.forEach((id) => { acc.m[id] = add([acc.m[id], c.months[id]]) })
        coIds.forEach((id) => { acc.co[id] = add([acc.co[id], c.cos[id]]) })
      })
      raw = acc
    } else {
      const v = values[node.id] || {}
      raw = { ec: v.ec, ep: v.ep, m: v.m || {}, co: v.co || {}, it: v.it, ac: v.ac }
    }
    const computed = derive(raw, monthIds, coIds, weights)
    map[node.id] = computed
    return computed
  }
  structure.forEach(visit)
  return map
}

// Totales de proyecto: ventas, total costes y margen del proyecto.
export function computeTotals (map, structure, monthIds, coIds) {
  const sales = map.sales || { ec: null, ep: null, months: {}, cos: {}, deviations: null, totalCo: null, totalEstimate: null }
  const cost = { ec: null, ep: null, months: {}, cos: {}, deviations: null, totalCo: null, totalEstimate: null, incurredTotal: null, accumulated: null }

  structure.forEach((node) => {
    if (node.role === 'sales' || node.info) return
    const c = map[node.id]
    if (!c) return
    cost.ec = add([cost.ec, c.ec])
    cost.ep = add([cost.ep, c.ep])
    cost.deviations = add([cost.deviations, c.deviations])
    cost.totalCo = add([cost.totalCo, c.totalCo])
    cost.totalEstimate = add([cost.totalEstimate, c.totalEstimate])
    cost.incurredTotal = add([cost.incurredTotal, c.incurredTotal])
    cost.accumulated = add([cost.accumulated, c.accumulated])
    monthIds.forEach((id) => { cost.months[id] = add([cost.months[id], c.months[id]]) })
    coIds.forEach((id) => { cost.cos[id] = add([cost.cos[id], c.cos[id]]) })
  })
  cost.margin = sub(cost.ec, cost.totalEstimate)
  cost.marginPct = present(cost.margin) ? (cost.ec ? cost.margin / cost.ec : 0) : null
  cost.commercialDeviation = sub(cost.ec, cost.ep)
  cost.deviationInitial = sub(cost.ep, cost.totalEstimate)
  cost.incurredMonth = sub(cost.incurredTotal, cost.accumulated)
  cost.pendingCost = sub(cost.totalEstimate, cost.incurredTotal)
  cost.workPct = ratio(cost.incurredTotal, cost.totalEstimate)

  // El margen de proyecto (pie) siempre se muestra como número: los vacíos cuentan como 0.
  const n = (v) => v || 0
  const project = {
    marginEc: n(sales.ec) - n(cost.ec),
    marginEp: n(sales.ep) - n(cost.ep),
    marginEstimate: n(sales.totalEstimate) - n(cost.totalEstimate),
    marginPctEc: n(sales.ec) ? (n(sales.ec) - n(cost.ec)) / n(sales.ec) : 0,
    marginPctEp: n(sales.ep) ? (n(sales.ep) - n(cost.ep)) / n(sales.ep) : 0,
    marginPctEstimate: n(sales.totalEstimate) ? (n(sales.totalEstimate) - n(cost.totalEstimate)) / n(sales.totalEstimate) : 0,
  }
  return { sales, cost, project }
}

// Suma de Change Orders por estado (confirmadas / pendientes / total) incluyendo la fila
// de Ventas (VENTAS/POC): cada CO = coste (cost.cos) + su valor en la fila de Ventas
// (sales.cos). Es el número de la cartilla verde de Control económico y de las órdenes
// de cambio del Expediente. `totals` es la salida de computeTotals.
export function coSums (totals, cos) {
  const cost = (totals.cost && totals.cost.cos) || {}
  const sales = (totals.sales && totals.sales.cos) || {}
  const co = (id) => (Number(cost[id]) || 0) + (Number(sales[id]) || 0)
  const isExpected = (c) => (c.status || 'confirmed') === 'expected'
  let confirmed = 0
  let expected = 0
  ;(cos || []).forEach((c) => { if (isExpected(c)) expected += co(c.id); else confirmed += co(c.id) })
  return { confirmed, expected, both: confirmed + expected }
}

// Aplana el árbol a filas { node, depth } para renderizar la tabla.
export function flatten (structure) {
  const rows = []
  const walk = (nodes, depth) => {
    nodes.forEach((node) => {
      rows.push({ node, depth })
      if (node.children) walk(node.children, depth + 1)
    })
  }
  walk(structure, 0)
  return rows
}
